import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Beyond Sure — Popover primitive (A1, portal).
 *
 * Portal-rendered floating panel anchored to a trigger element. Foundation
 * for Menu (which adds keyboard nav over items), the composer credits
 * popover, and per-row action menus.
 *
 * Behavior:
 *   - Portal to document.body so it escapes overflow:hidden parents.
 *   - Position calculated from the trigger's getBoundingClientRect.
 *   - Auto-flip when the requested side would clip outside the viewport.
 *   - Click outside closes (mousedown captured at document level).
 *   - Esc closes; focus returns to the trigger.
 *   - prefers-reduced-motion: opacity-only, no scale/slide.
 *   - Repositions on scroll + resize while open.
 *
 * Usage (render-prop trigger):
 *
 *   <Popover
 *     content={<div className="p-3">Hello</div>}
 *     side="bottom"
 *     align="end"
 *   >
 *     {({ ref, onClick, "aria-expanded": ariaExpanded }) => (
 *       <button ref={ref} onClick={onClick} aria-expanded={ariaExpanded}>
 *         Open
 *       </button>
 *     )}
 *   </Popover>
 *
 * Or controlled via `open` + `onOpenChange` for parent-managed state.
 */

type Side = "top" | "bottom" | "left" | "right";
type Align = "start" | "center" | "end";

interface TriggerProps {
  ref: (node: HTMLElement | null) => void;
  onClick: () => void;
  "aria-expanded": boolean;
  /** "dialog" by default; consumers like Menu/Combobox override to "menu"/"listbox". */
  "aria-haspopup": "dialog" | "menu" | "listbox" | "true";
  "aria-controls": string;
}

interface PopoverProps {
  /** Render-prop trigger or a single ReactElement that accepts ref/onClick. */
  children:
    | ReactElement
    | ((props: TriggerProps) => ReactNode);
  /** The floating panel content. */
  content: ReactNode;
  /** Preferred side relative to trigger. Default "bottom". */
  side?: Side;
  /** Alignment along the cross-axis. Default "start". */
  align?: Align;
  /** Pixel gap between trigger and panel. Default 6. */
  offset?: number;
  /** Controlled open state. If omitted, popover is uncontrolled. */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  /** Optional className on the panel surface. */
  panelClassName?: string;
  /** Forward a stable id for aria-controls. Defaults to useId(). */
  id?: string;
  /** ARIA role override. Defaults to "dialog". For menus use Menu primitive. */
  role?: "dialog" | "menu" | "listbox";
}

interface Position {
  top: number;
  left: number;
  /** The side actually used after auto-flip — needed for transform-origin. */
  resolvedSide: Side;
}

export function Popover({
  children,
  content,
  side = "bottom",
  align = "start",
  offset = 6,
  open: controlledOpen,
  onOpenChange,
  panelClassName,
  id,
  role = "dialog",
}: PopoverProps) {
  const autoId = useId();
  const panelId = id ?? autoId;
  const isControlled = typeof controlledOpen === "boolean";
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;

  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position>({
    top: 0,
    left: 0,
    resolvedSide: side,
  });
  const reducedMotion = useReducedMotion();

  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) {
        onOpenChange?.(next);
      } else {
        setInternalOpen(next);
        onOpenChange?.(next);
      }
    },
    [isControlled, onOpenChange],
  );

  const setTriggerRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
  }, []);

  /* ── Position calculation with auto-flip ─────────────────────────── */
  const recompute = useCallback(() => {
    const trig = triggerRef.current;
    const panel = panelRef.current;
    if (!trig || !panel) return;

    const t = trig.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Decide actual side via auto-flip.
    let resolvedSide: Side = side;
    if (side === "bottom" && t.bottom + offset + p.height > vh && t.top - offset - p.height > 0) {
      resolvedSide = "top";
    } else if (side === "top" && t.top - offset - p.height < 0 && t.bottom + offset + p.height < vh) {
      resolvedSide = "bottom";
    } else if (side === "right" && t.right + offset + p.width > vw) {
      resolvedSide = "left";
    } else if (side === "left" && t.left - offset - p.width < 0) {
      resolvedSide = "right";
    }

    // Calculate position based on resolvedSide + align.
    let top = 0;
    let left = 0;
    if (resolvedSide === "bottom") {
      top = t.bottom + offset;
      left = alignAxis(t.left, t.width, p.width, align, vw);
    } else if (resolvedSide === "top") {
      top = t.top - offset - p.height;
      left = alignAxis(t.left, t.width, p.width, align, vw);
    } else if (resolvedSide === "right") {
      left = t.right + offset;
      top = alignAxis(t.top, t.height, p.height, align, vh);
    } else {
      left = t.left - offset - p.width;
      top = alignAxis(t.top, t.height, p.height, align, vh);
    }

    // Clamp to viewport edges with 8px margin.
    left = Math.max(8, Math.min(left, vw - p.width - 8));
    top = Math.max(8, Math.min(top, vh - p.height - 8));

    setPosition({ top, left, resolvedSide });
  }, [side, align, offset]);

  /* ── Lifecycle: position + listeners while open ─────────────────── */
  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, recompute]);

  /* ── Click outside + Esc ────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        // Return focus to trigger.
        requestAnimationFrame(() => triggerRef.current?.focus?.());
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, setOpen]);

  /* ── Trigger composition ────────────────────────────────────────── */
  const triggerProps: TriggerProps = {
    ref: setTriggerRef,
    onClick: () => setOpen(!open),
    "aria-expanded": open,
    "aria-haspopup": "dialog",
    "aria-controls": panelId,
  };

  let triggerNode: ReactNode;
  if (typeof children === "function") {
    triggerNode = children(triggerProps);
  } else if (isValidElement(children)) {
    triggerNode = cloneElement(
      children as ReactElement<Record<string, unknown>>,
      triggerProps as unknown as Record<string, unknown>,
    );
  } else {
    triggerNode = children;
  }

  /* ── Render ─────────────────────────────────────────────────────── */
  const transformOrigin =
    position.resolvedSide === "bottom"
      ? "top center"
      : position.resolvedSide === "top"
      ? "bottom center"
      : position.resolvedSide === "right"
      ? "left center"
      : "right center";

  const portalRoot = typeof document !== "undefined" ? document.body : null;

  return (
    <>
      {triggerNode}
      {portalRoot
        ? createPortal(
            <AnimatePresence>
              {open ? (
                <motion.div
                  ref={panelRef}
                  id={panelId}
                  role={role}
                  initial={
                    reducedMotion
                      ? { opacity: 0 }
                      : { opacity: 0, scale: 0.97, y: position.resolvedSide === "top" ? 4 : -4 }
                  }
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={
                    reducedMotion
                      ? { opacity: 0 }
                      : { opacity: 0, scale: 0.97, y: position.resolvedSide === "top" ? 4 : -4 }
                  }
                  transition={{ duration: reducedMotion ? 0.12 : 0.16, ease: [0.4, 0, 0.2, 1] }}
                  style={{
                    position: "fixed",
                    top: position.top,
                    left: position.left,
                    transformOrigin,
                    zIndex: 1000,
                  }}
                  className={[
                    "rounded-[12px] overflow-hidden",
                    "bg-[var(--color-paper)]",
                    "border border-[var(--color-ink-08)]",
                    "shadow-[0_8px_32px_-12px_rgba(0,0,0,0.18)]",
                    panelClassName ?? "",
                  ].join(" ")}
                >
                  {content}
                </motion.div>
              ) : null}
            </AnimatePresence>,
            portalRoot,
          )
        : null}
    </>
  );
}

function alignAxis(
  triggerStart: number,
  triggerSize: number,
  panelSize: number,
  align: Align,
  viewportSize: number,
): number {
  let pos: number;
  if (align === "start") pos = triggerStart;
  else if (align === "end") pos = triggerStart + triggerSize - panelSize;
  else pos = triggerStart + triggerSize / 2 - panelSize / 2;
  return Math.max(8, Math.min(pos, viewportSize - panelSize - 8));
}

export default Popover;
